import {
  duplicateGrid,
  splitGrid,
} from "@/src/llm/Annotations"; /* Been Here */

import { IBlkDef } from "@/src/llm/GptModelLayout"; /* Been Here */
import { getBlockValueAtIdx } from "@/src/llm/components/DataFlow"; /* Been Here */

import {
  drawText,
  IFontOpts,
  measureText,
} from "@/src/llm/render/fontRender"; /* Been Here */

import { lerp } from "@/src/utils/math"; /* Been Here */
import { Mat4f } from "@/src/utils/matrix"; /* Been Here */
import { Dim, Vec3, Vec4 } from "@/src/utils/vector"; /* Been Here */

import { Phase } from "@/src/llm/walkthrough/Walkthrough"; /* Been Here */
import {
  processUpTo,
  startProcessBefore,
} from "@/src/llm/walkthrough/Walkthrough00_Intro"; /* Been Here */
import {
  commentary,
  DimStyle,
  IWalkthroughArgs,
  moveCameraTo,
  setInitialCamera,
} from "@/src/llm/walkthrough/WalkthroughTools"; /* Been Here */

export function walkthrough02_Embedding(args: IWalkthroughArgs) {
  let {
    walkthrough: wt,
    state,
    tools: { c_str, c_blockRef, c_dimRef, afterTime, cleanup, breakAfter },
    layout,
  } = args;

  let render = state.render;

  if (wt.phase !== Phase.Input_Detail_Embedding) {
    return;
  }

  setInitialCamera(
    state,
    new Vec3(15.654, 0.0, -80.905),
    new Vec3(287.0, 14.5, 3.199)
  );

  wt.dimHighlightBlocks = [
    layout.idxObj,
    layout.tokEmbedObj,
    layout.posEmbedObj,
    layout.residual0,
  ];

  commentary(wt)`
We saw previously how the tokens are mapped to a sequence of integers using a simple lookup table.
These integers, the ${c_blockRef(
    "_token indices_",
    state.layout.idxObj,
    DimStyle.TokenIdx
  )}, are the first and only time we see integers in the model.
From here on out, we're using floats (decimal numbers).

Let's take a look at how the 4th token (index 3) is used to generate the 4th column vector of our ${c_blockRef(
    "_input embedding_",
    state.layout.residual0
  )}.`;
  breakAfter();

  let t_moveCamera = afterTime(null, 1.0);
  let t0_splitEmbedAnim = afterTime(null, 0.3);

  breakAfter();

  commentary(wt)`
We use the token index (in this case ${c_str("B", DimStyle.Token)} = ${c_dimRef(
    "1",
    DimStyle.TokenIdx
  )}) to select the 2nd column of the ${c_blockRef(
    "_token embedding matrix_",
    state.layout.tokEmbedObj
  )} on the left.
Note we're using 0-based indexing here, so the first column is at index 0.

This produces a column vector of size ${c_dimRef(
    "_C_ = 48",
    DimStyle.C
  )}, which we describe as the token embedding.
    `;
  breakAfter();

  let t1_fadeEmbedAnim = afterTime(null, 0.3);
  let t2_highlightTokenEmbed = afterTime(null, 0.8);

  breakAfter();

  commentary(wt)`
And since we're looking at our token ${c_str(
    "B",
    DimStyle.Token
  )} in the 4th _position_ (t = ${c_dimRef(
    "3",
    DimStyle.T
  )}), we'll take the 4th column of the ${c_blockRef(
    "_position embedding matrix_",
    state.layout.posEmbedObj
  )}.

This also produces a column vector of size ${c_dimRef(
    "_C_ = 48",
    DimStyle.C
  )}, which we describe as the position embedding.
    `;
  breakAfter();

  let t4_highlightPosEmbed = afterTime(null, 0.8);

  breakAfter();

  commentary(wt)`
Note that both of these position and token embeddings are learned during training (indicated by their blue color).

Now that we have these two column vectors, we simply add them together to produce another column vector of size ${c_dimRef(
    "_C_ = 48",
    DimStyle.C
  )}.
`;

  breakAfter();

  let t3_moveTokenEmbed = afterTime(null, 0.8);
  let t5_movePosEmbed = afterTime(null, 0.8);
  let t6_plusSymAnim = afterTime(null, 0.8);
  let t7_addAnim = afterTime(null, 0.8);
  let t8_placeAnim = afterTime(null, 0.8);
  let t9_cleanupInstant = afterTime(null, 0.0);
  let t10_fadeAnim = afterTime(null, 0.8);

  breakAfter();

  commentary(wt)`
We now run this same process for all of the tokens in the input sequence, creating a set of vectors which incorporate both the token values and their positions.

`;

  breakAfter();

  let t11_fillRest = afterTime(null, 5.0);

  breakAfter();

  commentary(wt)`
Feel free to hover over individual cells on the ${c_blockRef(
    "_input embedding_",
    state.layout.residual0
  )} matrix to see the computations and their sources.

We see that running this process for all the tokens in the input sequence produces a matrix of size ${c_dimRef(
    "_T_",
    DimStyle.T
  )} x ${c_dimRef("_C_", DimStyle.C)}.
The ${c_dimRef("_T_", DimStyle.T)} stands for ${c_dimRef(
    "_time_",
    DimStyle.T
  )}, i.e., you can think of tokens later in the sequence as later in time.
The ${c_dimRef("_C_", DimStyle.C)} stands for ${c_dimRef(
    "_channel_",
    DimStyle.C
  )}, but is also referred to as "feature" or "dimension" or "embedding size". This length, ${c_dimRef(
    "_C_",
    DimStyle.C
  )},
is one of the several "hyperparameters" of the model, and is chosen by the designer to in a tradeoff between model size and performance.

This matrix, which we'll refer to as the ${c_blockRef(
    "_input embedding_",
    state.layout.residual0
  )} is now ready to be passed down through the model.
This collection of ${c_dimRef(
    "T",
    DimStyle.T
  )} columns each of length ${c_dimRef(
    "C",
    DimStyle.C
  )} will become a familiar sight throughout this guide.
`;

  cleanup(t9_cleanupInstant, [
    t3_moveTokenEmbed,
    t5_movePosEmbed,
    t6_plusSymAnim,
    t7_addAnim,
    t8_placeAnim,
  ]);

  cleanup(t10_fadeAnim, [
    t0_splitEmbedAnim,
    t1_fadeEmbedAnim,
    t2_highlightTokenEmbed,
    t4_highlightPosEmbed,
  ]);

  moveCameraTo(
    state,
    t_moveCamera,
    new Vec3(7.6, 0, -33.1),
    new Vec3(290, 15.5, 0.8)
  );

  let residCol: IBlkDef = null!;
  let exampleIdx = 3;
  if (
    (t0_splitEmbedAnim.t > 0.0 || t10_fadeAnim.t > 0.0) &&
    t11_fillRest.t === 0
  ) {
    splitGrid(
      layout,
      layout.idxObj,
      Dim.X,
      exampleIdx + 0.5,
      t0_splitEmbedAnim.t * 4.0
    );

    layout.residual0.access!.disable = true;
    layout.residual0.opacity = lerp(1.0, 0.1, t1_fadeEmbedAnim.t);

    residCol = splitGrid(
      layout,
      layout.residual0,
      Dim.X,
      exampleIdx + 0.5,
      t0_splitEmbedAnim.t * 4.0
    )!;

    residCol.highlight = 0.3;

    residCol.opacity = lerp(1.0, 0.0, t1_fadeEmbedAnim.t);
  }

  let tokValue =
    getBlockValueAtIdx(layout.idxObj, new Vec3(exampleIdx, 0, 0)) ?? 1;

  let tokColDupe: IBlkDef | null = null;
  let posColDupe: IBlkDef | null = null;

  if (t2_highlightTokenEmbed.t > 0.0) {
    let tokEmbedCol = splitGrid(
      layout,
      layout.tokEmbedObj,
      Dim.X,
      tokValue + 0.5,
      t2_highlightTokenEmbed.t * 4.0
    )!;

    tokColDupe = duplicateGrid(layout, tokEmbedCol);
    tokColDupe.t = "i";
    tokEmbedCol.highlight = 0.3;

    let startPos = new Vec3(tokEmbedCol.x, tokEmbedCol.y, tokEmbedCol.z);
    let targetPos = new Vec3(residCol.x, residCol.y, residCol.z).add(
      new Vec3(-2.0, 0, 3.0)
    );

    let pos = startPos.lerp(targetPos, t3_moveTokenEmbed.t);

    tokColDupe.x = pos.x;
    tokColDupe.y = pos.y;
    tokColDupe.z = pos.z;
  }

  if (t4_highlightPosEmbed.t > 0.0) {
    let posEmbedCol = splitGrid(
      layout,
      layout.posEmbedObj,
      Dim.X,
      exampleIdx + 0.5,
      t4_highlightPosEmbed.t * 4.0
    )!;

    posColDupe = duplicateGrid(layout, posEmbedCol);
    posColDupe.t = "i";
    posEmbedCol.highlight = 0.3;

    let startPos = new Vec3(posEmbedCol.x, posEmbedCol.y, posEmbedCol.z);
    let targetPos = new Vec3(residCol.x, residCol.y, residCol.z).add(
      new Vec3(2.0, 0, 3.0)
    );

    let pos = startPos.lerp(targetPos, t5_movePosEmbed.t);

    posColDupe.x = pos.x;
    posColDupe.y = pos.y;
    posColDupe.z = pos.z;
  }

  if (
    t6_plusSymAnim.t > 0.0 &&
    tokColDupe &&
    posColDupe &&
    t7_addAnim.t < 1.0
  ) {
    for (let c = 0; c < layout.shape.C; c++) {
      let plusCenter = new Vec3(
        (tokColDupe.x + tokColDupe.dx + posColDupe.x) / 2,
        tokColDupe.y + layout.cell * (c + 0.5),
        tokColDupe.z + tokColDupe.dz / 2
      );

      let isActive = t6_plusSymAnim.t > (c + 1) / layout.shape.C;
      let opacity = lerp(0.0, 1.0, isActive ? 1 : 0);

      let fontOpts: IFontOpts = {
        color: new Vec4(0, 0, 0, 1).mul(opacity),
        size: 1.5,
        mtx: Mat4f.fromTranslation(plusCenter),
      };

      let w = measureText(render.modelFontBuf, "+", fontOpts);

      drawText(render.modelFontBuf, "+", -w / 2, -fontOpts.size / 2, fontOpts);
    }
  }

  let origResidPos = residCol
    ? new Vec3(residCol.x, residCol.y, residCol.z)
    : new Vec3();
  let offsetResidPos = origResidPos.add(new Vec3(0.0, 0, 3.0));

  if (t7_addAnim.t > 0.0 && tokColDupe && posColDupe) {
    let targetPos = offsetResidPos;
    let tokStartPos = new Vec3(tokColDupe.x, tokColDupe.y, tokColDupe.z);
    let posStartPos = new Vec3(posColDupe.x, posColDupe.y, posColDupe.z);

    let tokPos = tokStartPos.lerp(targetPos, t7_addAnim.t);
    let posPos = posStartPos.lerp(targetPos, t7_addAnim.t);

    tokColDupe.x = tokPos.x;
    tokColDupe.y = tokPos.y;
    tokColDupe.z = tokPos.z;
    posColDupe.x = posPos.x;
    posColDupe.y = posPos.y;
    posColDupe.z = posPos.z;

    if (t7_addAnim.t > 0.95) {
      tokColDupe.opacity = 0.0;
      posColDupe.opacity = 0.0;
      residCol.opacity = 1.0;
      residCol.highlight = 0.0;
      residCol.access!.disable = false;
      residCol.x = targetPos.x;
      residCol.y = targetPos.y;
      residCol.z = targetPos.z;
    }
  }

  if (t8_placeAnim.t > 0.0) {
    let startPos = offsetResidPos;
    let targetPos = origResidPos;
    let pos = startPos.lerp(targetPos, t8_placeAnim.t);
    residCol.x = pos.x;
    residCol.y = pos.y;
    residCol.z = pos.z;
  }

  if (t9_cleanupInstant.t > 0.0 && residCol) {
    residCol.opacity = 1.0;
    residCol.highlight = 0.0;
    residCol.access!.disable = false;
  }

  if (t11_fillRest.t > 0.0) {
    layout.residual0.access!.disable = true;

    let prevInfo = startProcessBefore(state, layout.residual0);

    processUpTo(state, t11_fillRest, layout.residual0, prevInfo);
  }
}

/* Observed */
